import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'; 
import { regions } from '../data/regions';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  FaDirections, FaCamera, FaSnowflake, FaSun, FaRobot, 
  FaMapMarkedAlt, FaListUl, FaSearch, FaEnvelope, FaArrowLeft, 
  FaPhone, FaGlobe, FaCrown, FaGoogle, FaMap, FaImage
} from 'react-icons/fa';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// --- IMPORTS SUPABASE ---
import { supabase } from '../services/supabaseClient';

// --- IMPORTS BLOG & EMAIL ---
import EmailCaptureModal from '../components/EmailCaptureModal';
import { getRegionBlogData, BlogData } from '../services/blogService';
import RegionBlogSection from '../components/RegionBlogSection';

// --- IMPORT DU SERVICE UNSPLASH ---
import { getRegionImage } from '../services/unsplash';
import { callOpenAIChatCompletion } from '../services/secureProxy';

// --- IMPORT WIDGET FORFY LIVE ---
import ForfyLiveWidget from '../components/ForfyLive/ForfyLiveWidget';
import PartnerReviewSummary from '../components/PartnerReviewSummary';

// Fix icônes Leaflet par défaut
import iconMarker from 'leaflet/dist/images/marker-icon.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: iconMarker,
  iconRetinaUrl: iconRetina,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

// Icône Rouge (Utilisateur)
const UserIcon = L.icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Icône Verte (Services / Résultats IA)
const GreenIcon = L.icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

interface SmartActivity {
  name: string;
  description: string;
  type: string;
  emoji: string;
  address?: string;
  phone?: string;
  website?: string;
  coordinates?: { lat: number, lng: number }; 
}

const RecenterMap = ({ lat, lng }: { lat: number, lng: number }) => {
  const map = useMap();
  useEffect(() => { map.setView([lat, lng], map.getZoom()); }, [lat, lng, map]);
  return null;
};

// --- HELPERS ROBUSTES POUR ID / SLUG / NOM ---
const normalizeText = (value: string = '') =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const slugifyRegion = (value: string = '') =>
  normalizeText(value)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const getRegionName = (r: any) => r?.nom || r?.name || '';
const getRegionId = (r: any) => String(r?.id ?? '');
const getRegionSlug = (r: any) => r?.slug || slugifyRegion(getRegionName(r));

export default function RegionDetail() {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // On supporte plusieurs noms de params pour ne rien casser
  const rawParam =
    (params as any)?.id ||
    (params as any)?.slug ||
    (params as any)?.regionId ||
    (params as any)?.regionSlug ||
    '';

  // ✅ Recherche robuste: id / slug / name / query params
  const region = useMemo(() => {
    const routeValue = decodeURIComponent(String(rawParam || '')).trim();
    const queryId = decodeURIComponent(searchParams.get('id') || '').trim();
    const queryName = decodeURIComponent(searchParams.get('name') || '').trim();

    const routeNorm = normalizeText(routeValue);
    const queryIdNorm = normalizeText(queryId);
    const queryNameNorm = normalizeText(queryName);

    // 1) Match par ID (route ou query)
    let found =
      regions.find((r: any) => getRegionId(r) === routeValue) ||
      regions.find((r: any) => getRegionId(r) === queryId);

    // 2) Match par slug (route ou query "name")
    if (!found) {
      found =
        regions.find((r: any) => getRegionSlug(r) === routeNorm) ||
        regions.find((r: any) => getRegionSlug(r) === queryNameNorm);
    }

    // 3) Match par nom exact normalisé (route ou query)
    if (!found) {
      found =
        regions.find((r: any) => normalizeText(getRegionName(r)) === routeNorm) ||
        regions.find((r: any) => normalizeText(getRegionName(r)) === queryNameNorm);
    }

    return found || null;
  }, [rawParam, searchParams]);

  const regionName = region ? getRegionName(region) : '';
  const regionId = region ? getRegionId(region) : '';
  const regionSlug = region ? getRegionSlug(region) : '';

  // ✅ Canonicalise l’URL si on arrive avec /region/3 (non bloquant)
  useEffect(() => {
    if (!region) return;
    const current = String(rawParam || '').trim();
    if (/^\d+$/.test(current) && regionSlug) {
      const qs = new URLSearchParams(searchParams);
      qs.set('id', regionId);
      qs.set('name', regionName);

      const nextUrl = `/region/${regionSlug}${qs.toString() ? `?${qs.toString()}` : ''}`;
      navigate(nextUrl, { replace: true });
    }
  }, [region, rawParam, regionSlug, regionId, regionName, searchParams, navigate]);

  // --- ÉTATS ---
  const [activeTab, setActiveTab] = useState<'selection' | 'map'>('selection');
  const [userLocation, setUserLocation] = useState<{lat: number, lng: number} | null>(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [user, setUser] = useState<any>(null);

  // État pour l'image Unsplash et gestion d'erreur
  const [heroImage, setHeroImage] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false); // Pour savoir si l'image a échoué

  // États Blog & IA
  const [blogData, setBlogData] = useState<BlogData | null>(null);
  const [loadingBlog, setLoadingBlog] = useState(false);
    
  // États "Top Sélection"
  const [smartActivities, setSmartActivities] = useState<SmartActivity[] | null>(null);
  const [seasonalTip, setSeasonalTip] = useState<string | null>(null);
  const [loadingAi, setLoadingAi] = useState(false);

  // États "Carte & Services"
  const [searchQuery, setSearchQuery] = useState("");
  const [serviceSuggestions, setServiceSuggestions] = useState<SmartActivity[] | null>(null);
  const [loadingServices, setLoadingServices] = useState(false);

  const currentMonth = new Date().getMonth();
  const isWinter = currentMonth >= 10 || currentMonth <= 3;
  const seasonName = isWinter ? "Hiver" : "Été";
  const seasonIcon = isWinter ? <FaSnowflake className="text-blue-400"/> : <FaSun className="text-yellow-500"/>;

  const quickServices = ['Restaurant', 'Hôtel', 'Station service', 'Pharmacie', 'Café', 'Épicerie', 'Banque', 'Hôpital', 'Parking', 'Camping'];

  // 1. Géolocalisation & Auth
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude }),
        (err) => console.log("GPS non autorisé", err)
      );
    }
    
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    };
    checkUser();
  }, []);

  // 2. Chargement des données
  useEffect(() => {
    if (region) {
      window.scrollTo(0, 0);
      fetchSmartContent(regionName);
      loadBlog(regionName);
      setImageError(false); // Reset de l'erreur au changement de région
      setHeroImage(null);   // Reset de l'image précédente
      
      // --- CHARGEMENT DE L'IMAGE UNSPLASH ---
      const fetchImage = async () => {
        const url = await getRegionImage(regionName);
        if (url) {
          setHeroImage(url);
        } else {
          if (!(region as any).image) setImageError(true);
        }
      };
      fetchImage();
    }
  }, [region, regionName]);

  const loadBlog = async (name: string) => {
    setLoadingBlog(true);
    const data = await getRegionBlogData(name, seasonName);
    setBlogData(data);
    setLoadingBlog(false);
  };

  const fetchSmartContent = async (name: string) => {
    setLoadingAi(true);
    try {
      const prompt = `Agis comme une API JSON stricte. Contexte : Tourisme au Québec en ${seasonName}. Région : ${name}. Tâche 1 : Liste 4 activités incontournables. Tâche 2 : Un conseil saisonnier bref. FORMAT : { "activities": [ { "name": "Lieu", "description": "Desc", "type": "Nature", "emoji": "🏔️" } ], "tip": "Conseil" }`;
      const data = await callOpenAIChatCompletion({
        model: "gpt-4o-mini",
        messages: [{ role: "system", content: "JSON only." }, { role: "user", content: prompt }],
        temperature: 0.5
      });
      const content = (data.choices?.[0]?.message?.content || "").replace(/```json/g, '').replace(/```/g, '').trim();
      const parsed = JSON.parse(content);
      setSmartActivities(parsed.activities);
      setSeasonalTip(parsed.tip);
    } catch (err) {
      console.warn('Contenu IA régional indisponible, fallback local utilisé:', err);
      setSmartActivities([
        { name: `Explorer ${name}`, description: `Découvrez les attraits incontournables, les commerces locaux et les expériences saisonnières de ${name}.`, type: 'Découverte', emoji: '🧭' },
        { name: 'Route panoramique', description: 'Prévoyez un itinéraire flexible avec des arrêts photos, cafés locaux et points de vue.', type: 'Nature', emoji: '🏞️' },
        { name: 'Expérience gourmande', description: 'Repérez les marchés, restaurants et producteurs de la région pour vivre une expérience locale.', type: 'Gastronomie', emoji: '🍽️' },
        { name: 'Activité signature', description: 'Choisissez une activité forte selon la saison : plein air, culture, détente ou événement local.', type: 'Expérience', emoji: '✨' }
      ]);
      setSeasonalTip(`Conseil Forfeo : vérifiez les horaires, la météo et les disponibilités avant de réserver dans ${name}.`);
    } finally {
      setLoadingAi(false);
    }
  };

  const handleServiceSearch = async (e?: React.FormEvent, directQuery?: string) => {
    if (e) e.preventDefault();
    const query = directQuery || searchQuery;
    if (!query) return;

    setSearchQuery(query);
    setLoadingServices(true);
    setServiceSuggestions(null);

    try {
      const prompt = `
        Agis comme une API JSON stricte. 
        Contexte : Région ${regionName}, Québec. 
        Tâche : L'utilisateur cherche "${query}". 
        Donne-moi 5 lieux pertinents.
        IMPORTANT : Fournis l'adresse précise, le numéro de téléphone et le site web si possible.
        FORMAT : { "results": [ { "name": "Nom du lieu", "description": "Courte desc", "type": "Catégorie", "emoji": "📍", "address": "Adresse complète", "phone": "Téléphone ou N/A", "website": "URL ou N/A", "coordinates": { "lat": 46.000, "lng": -71.000 } } ] }
      `;

      const data = await callOpenAIChatCompletion({
        model: "gpt-4o-mini",
        messages: [{ role: "system", content: "JSON only." }, { role: "user", content: prompt }],
        temperature: 0.5
      });
      const content = (data.choices?.[0]?.message?.content || "").replace(/```json/g, '').replace(/```/g, '').trim();
      const parsed = JSON.parse(content);
      setServiceSuggestions(parsed.results);
    } catch (err) {
      console.warn('Recherche intelligente indisponible, ouverture Google Maps:', err);
      launchGoogleMaps(query);
    } finally {
      setLoadingServices(false);
    }
  };

  const launchGoogleMaps = (placeName: string) => {
    const destination = encodeURIComponent(`${placeName} ${regionName} Québec`);
    let url = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
    if (userLocation) url += `&origin=${userLocation.lat},${userLocation.lng}`;
    window.open(url, '_blank');
  };

  const openPhotos = (placeName: string) => {
    const query = encodeURIComponent(`${placeName} ${regionName} ${seasonName} photo`);
    window.open(`https://www.google.com/search?tbm=isch&q=${query}`, '_blank');
  };

  const mapCenter = userLocation ? [userLocation.lat, userLocation.lng] : [46.8139, -71.2080];

  if (!region) {
    return (
      <div className="flex flex-col min-h-screen bg-slate-50 font-sans">
        <Header />
        <div className="flex-1 flex items-center justify-center px-4 py-16">
          <div className="max-w-xl w-full bg-white rounded-3xl border border-slate-200 shadow-xl p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
              <FaMapMarkedAlt className="text-2xl" />
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-3">Région introuvable</h1>
            <p className="text-slate-600 mb-6">
              Le lien de la région semble invalide, incomplet ou l’identifiant ne correspond pas.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/')}
                className="bg-slate-900 text-white px-5 py-3 rounded-xl font-bold hover:bg-black transition"
              >
                Retour à l’accueil
              </button>
              <button
                onClick={() => navigate('/#carte-regions')}
                className="bg-blue-600 text-white px-5 py-3 rounded-xl font-bold hover:bg-blue-700 transition"
              >
                Voir la carte des régions
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // --- LOGIQUE D'AFFICHAGE IMAGE (SÉCURITÉ) ---
  const validImage = heroImage || (region as any).image;
  const showFallback = imageError || !validImage;

  return (
    <div className="flex flex-col min-h-screen bg-slate-50 font-sans relative">
      <Header />
      
      {/* NAVIGATION FLOTTANTE (HAUT) */}
      <div className="fixed top-24 left-4 z-[999] flex flex-col gap-2">
        <button 
          onClick={() => navigate('/')}
          className="bg-white text-blue-900 px-4 py-2 rounded-full shadow-xl border border-blue-100 flex items-center gap-2 hover:bg-blue-50 transition font-bold text-sm w-fit"
        >
          <FaArrowLeft /> Accueil
        </button>
        
        <button 
          onClick={() => navigate('/carte')}
          className="bg-blue-600 text-white px-4 py-2 rounded-full shadow-xl border border-blue-700 flex items-center gap-2 hover:bg-blue-700 transition font-bold text-sm w-fit"
        >
          <FaMap /> Carte Interactive
        </button>

        {user && (
          <Link 
            to="/club" 
            className="bg-yellow-400 text-blue-900 px-4 py-2 rounded-full font-bold text-sm hover:bg-yellow-300 transition flex items-center gap-2 shadow-lg border border-yellow-500 w-fit"
          >
            <FaCrown /> Retour Club
          </Link>
        )}
      </div>

      <EmailCaptureModal 
        isOpen={isEmailModalOpen} 
        onClose={() => setIsEmailModalOpen(false)} 
        dataToSend={activeTab === 'selection' ? smartActivities : serviceSuggestions} 
      />

      {/* HERO HEADER - GESTION ERREUR IMAGE */}
      {!showFallback ? (
        <div 
          className="relative h-[50vh] min-h-[400px] flex items-end justify-center transition-all duration-1000"
          style={{
            backgroundImage: `url(${validImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 via-blue-900/40 to-transparent"></div>
          <div className="relative z-10 pb-12 px-4 text-center w-full">
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-4 drop-shadow-lg">{regionName}</h1>
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-6 py-2 rounded-full border border-white/30 text-white animate-fade-in">
              {seasonIcon} <span className="font-bold uppercase tracking-wider text-sm">Mode {seasonName}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="relative h-[50vh] min-h-[400px] flex flex-col items-center justify-center bg-slate-800 text-white p-6 text-center">
          <div className="mb-4 text-6xl opacity-20"><FaImage /></div>
          <h1 className="text-3xl md:text-5xl font-extrabold mb-2">{regionName}</h1>
          <p className="text-slate-400 mb-6 max-w-lg">
            Photo momentanément indisponible. Explorez la galerie complète sur Google Images.
          </p>
          <button 
            onClick={() => openPhotos(regionName)}
            className="bg-white text-slate-900 px-6 py-3 rounded-full font-bold flex items-center gap-2 hover:bg-blue-50 hover:text-blue-900 transition shadow-lg"
          >
            <FaGoogle className="text-blue-500"/> Voir les photos sur Google
          </button>
        </div>
      )}

      {/* SECTION BLOG */}
      <RegionBlogSection data={blogData} loading={loadingBlog} />

      {/* LOT 38 — AVIS PUBLICS / RÉPUTATION RÉGIONALE */}
      <div className="container mx-auto px-4 mt-8 relative z-20">
        <PartnerReviewSummary
          region={regionName}
          title={`Avis publics — ${regionName}`}
          subtitle="Les avis approuvés par Forfeo aident les voyageurs à choisir des expériences locales avec confiance."
        />
      </div>

      {/* CONTENU PRINCIPAL */}
      <div className="container mx-auto px-4 relative z-20 pb-24">
        
        {/* Navigation Onglets */}
        <div className="flex justify-center gap-4 mb-8 mt-8">
          <button
            onClick={() => setActiveTab('selection')}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold shadow-lg transition-all ${activeTab === 'selection' ? 'bg-yellow-400 text-blue-900 scale-105' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
          >
            <FaListUl /> Top Sélection
          </button>
          <button
            onClick={() => setActiveTab('map')}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold shadow-lg transition-all ${activeTab === 'map' ? 'bg-yellow-400 text-blue-900 scale-105' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
          >
            <FaMapMarkedAlt /> Carte & Services
          </button>
        </div>

        {/* ONGLET 1 : TOP SÉLECTION */}
        {activeTab === 'selection' && (
          <>
            {loadingAi && (
              <div className="bg-white p-10 rounded-2xl shadow-xl text-center animate-pulse">
                <FaRobot className="text-4xl text-blue-500 mx-auto mb-4 animate-bounce"/>
                <p className="text-xl font-bold text-slate-700">L'assistant analyse la région...</p>
              </div>
            )}

            {!loadingAi && smartActivities && (
              <div className="space-y-8 animate-fade-in">
                {seasonalTip && (
                  <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-2xl p-6 shadow-xl text-white flex items-start gap-4">
                    <div className="bg-white/20 p-3 rounded-full text-2xl hidden md:block">{seasonIcon}</div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">Conseil Forfeo</h3>
                      <p className="text-indigo-100 text-lg">{seasonalTip}</p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {smartActivities.map((activity, idx) => (
                    <div key={idx} className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition border border-slate-100 flex flex-col">
                      <div className="flex justify-between items-start mb-4">
                        <div className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-xs font-bold uppercase">{activity.type}</div>
                        <span className="text-4xl">{activity.emoji}</span>
                      </div>
                      <h4 className="text-2xl font-bold text-slate-800 mb-2">{activity.name}</h4>
                      <p className="text-slate-600 mb-6 flex-grow">{activity.description}</p>
                      <div className="grid grid-cols-2 gap-3 mt-auto">
                        <button onClick={() => openPhotos(activity.name)} className="flex items-center justify-center gap-2 w-full py-3 rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition">
                          <FaCamera /> Photos
                        </button>
                        <button onClick={() => launchGoogleMaps(activity.name)} className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 shadow-lg transition">
                          <FaDirections /> Y aller
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="text-center mt-8">
                  <button onClick={() => setIsEmailModalOpen(true)} className="bg-yellow-400 text-blue-900 px-8 py-4 rounded-full font-bold shadow-lg hover:bg-yellow-300 transition flex items-center gap-2 mx-auto">
                    <FaEnvelope /> Recevoir cette sélection par courriel
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ONGLET 2 : CARTE & SERVICES */}
        {activeTab === 'map' && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200 flex flex-col md:flex-row h-auto md:h-[700px]">
            {/* Colonne Liste */}
            <div className="w-full md:w-1/3 p-6 bg-slate-50 border-b md:border-b-0 md:border-r border-slate-200 flex flex-col gap-4 overflow-y-auto">
              <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2"><FaSearch className="text-blue-600"/> Explorer les services</h3>
              
              <form onSubmit={(e) => handleServiceSearch(e)} className="relative mb-4">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Ex: Poutine, Garage..."
                  className="w-full pl-4 pr-12 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500 outline-none shadow-sm"
                />
                <button
                  type="submit"
                  disabled={loadingServices}
                  className="absolute right-2 top-2 bottom-2 bg-blue-600 text-white px-3 rounded-lg hover:bg-blue-700 transition font-bold disabled:opacity-50"
                >
                  {loadingServices ? '...' : 'Go'}
                </button>
              </form>

              {!loadingServices && !serviceSuggestions && (
                <div className="flex flex-wrap gap-2">
                  {quickServices.map(item => (
                    <button
                      key={item}
                      onClick={() => handleServiceSearch(undefined, item)}
                      className="bg-white text-slate-700 px-3 py-2 rounded-lg text-sm font-medium shadow-sm border border-slate-200 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300 transition flex items-center gap-2"
                    >
                      {item}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex-grow overflow-y-auto p-4 custom-scrollbar">
                {loadingServices && (
                  <div className="text-center py-10 animate-pulse">
                    <FaRobot className="text-4xl text-yellow-400 mx-auto mb-4 animate-bounce"/>
                    <p className="font-bold text-slate-600">Recherche des meilleures adresses...</p>
                  </div>
                )}

                {serviceSuggestions && (
                  <div className="space-y-4 animate-fade-in">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-bold text-slate-700">🔎 Top Résultats : {searchQuery}</h4>
                      <button onClick={() => setServiceSuggestions(null)} className="text-xs text-blue-500 hover:underline">Effacer</button>
                    </div>

                    {serviceSuggestions.map((item, idx) => (
                      <div key={idx} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition group">
                        <div className="flex items-start justify-between">
                          <div>
                            <h5 className="font-bold text-lg text-slate-800">{item.name}</h5>
                            <p className="text-xs text-blue-500 font-bold uppercase mb-1">{item.type}</p>
                          </div>
                          <span className="text-2xl bg-slate-50 p-2 rounded-full">{item.emoji}</span>
                        </div>

                        <p className="text-sm text-slate-500 mb-3">{item.description}</p>
                        
                        {item.address && (
                          <p className="text-xs text-slate-400 mb-2 flex items-center gap-1">
                            <FaMapMarkedAlt/> {item.address}
                          </p>
                        )}
                        
                        <button
                          onClick={() => launchGoogleMaps(item.name)}
                          className="w-full bg-blue-50 text-blue-600 py-2 rounded-lg font-bold text-sm hover:bg-blue-600 hover:text-white transition flex items-center justify-center gap-2"
                        >
                          <FaDirections /> Y aller (GPS)
                        </button>
                      </div>
                    ))}

                    <div className="text-center mt-4">
                      <button
                        onClick={() => setIsEmailModalOpen(true)}
                        className="bg-yellow-400 text-blue-900 px-6 py-3 rounded-full font-bold shadow-sm hover:bg-yellow-300 transition flex items-center gap-2 mx-auto text-sm"
                      >
                        <FaEnvelope /> Recevoir ces adresses
                      </button>
                    </div>
                  </div>
                )}

                {!loadingServices && !serviceSuggestions && (
                  <div className="h-full flex flex-col items-center justify-center text-slate-400 opacity-60 mt-10">
                    <FaMapMarkedAlt className="text-6xl mb-4"/>
                    <p className="text-center">Sélectionnez une catégorie<br/>pour voir les suggestions.</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Colonne Carte */}
            <div className="w-full md:w-2/3 h-[400px] md:h-full relative">
              <MapContainer center={mapCenter as L.LatLngExpression} zoom={10} className="h-full w-full z-0">
                <TileLayer attribution='© OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
                
                {userLocation && (
                  <Marker position={[userLocation.lat, userLocation.lng]} icon={UserIcon}>
                    <Popup>Vous êtes ici 📍</Popup>
                  </Marker>
                )}
                
                {serviceSuggestions && serviceSuggestions.map((item, idx) => (
                  item.coordinates && (
                    <Marker key={idx} position={[item.coordinates.lat, item.coordinates.lng]} icon={GreenIcon}>
                      <Popup>
                        <div className="text-center min-w-[200px]">
                          <h3 className="font-bold text-lg">{item.emoji} {item.name}</h3>
                          <p className="text-xs text-slate-500 my-1">{item.type}</p>
                          <p className="text-sm mb-2">{item.description}</p>
                          
                          <div className="text-left bg-slate-50 p-2 rounded text-xs space-y-2 mb-3 border border-slate-100">
                            {item.address && (
                              <p className="flex items-center gap-2 text-slate-600">
                                <FaMapMarkedAlt className="text-blue-500 flex-shrink-0"/> <span>{item.address}</span>
                              </p>
                            )}
                            {item.phone && (
                              <p className="flex items-center gap-2 text-slate-600">
                                <FaPhone className="text-green-500 flex-shrink-0"/> <span>{item.phone}</span>
                              </p>
                            )}
                            {item.website && item.website !== 'N/A' && (
                              <a href={item.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-blue-600 hover:underline font-bold">
                                <FaGlobe className="flex-shrink-0"/> Site Web
                              </a>
                            )}
                          </div>

                          <button
                            onClick={() => launchGoogleMaps(item.name)}
                            className="bg-blue-600 text-white px-3 py-2 rounded w-full text-xs font-bold hover:bg-blue-700 transition"
                          >
                            Ouvrir dans Google Maps
                          </button>
                        </div>
                      </Popup>
                    </Marker>
                  )
                ))}

                {userLocation && <RecenterMap lat={userLocation.lat} lng={userLocation.lng} />}
              </MapContainer>
            </div>
          </div>
        )}
      </div>

      <Footer />

      {/* --- WIDGET FORFY LIVE (SEUL) --- */}
      <div className="fixed bottom-6 right-4 z-[9999]">
        <ForfyLiveWidget regionSlug={regionSlug || String(rawParam || '')} />
      </div>
    </div>
  );
}
